import { useCallback, useContext, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useNode } from "@craftjs/core";
import EmptyState from "@/components/EmptyState";
import { Spacing } from "@/components/editor/settings/SpacingSettings/SpacingSettings";
import useNormalizeSpacing from "@/components/editor/settings/SpacingSettings/useNormalizeSpacing";
import { SectionContext } from "./section-block/SectionBlock";
import ViewSwitcher from "../../view-switcher/ViewSwitcher";
import useResponsiveValues from "@/components/editor/hooks/useResponsiveValues";
import { useAppSelector } from "@/hooks";
import { useAccessControl } from "@/features/access-control";
import { Category, Content, ContentStatus, Organization } from "@/types";
import { Box } from "@mui/material";
import DishTemplate from "@/components/DishTemplate";
import { isLoggedInSelector } from "@/features/auth/slice";

interface DishProps extends Spacing {
  displayType: "list" | "grid" | "carousel";
  selectedContent: Content["id"][];
  limit: number | "all";
  sortBy: "title" | "price";
  sort: "descending" | "ascending";
  selectedCategories: Category["id"][];
  previewWidget: string;
}

const DishBlock = (props: Partial<DishProps>) => {
  const { displayType = "list", selectedCategories, selectedContent, limit = 5, sortBy = "price", sort = "descending", previewWidget } = props;
  const isLoggedIn = useAppSelector(isLoggedInSelector);

  const {
    id,
    connectors: { connect, drag },
  } = useNode();

  const params = useParams<{ organizationId?: string; organizationAlias?: string }>();
  const activeOrganization = useAppSelector((store) => store.ui.activeOrganization);
  const sectionSettings = useContext(SectionContext);
  const currentOrganization = useAppSelector((store) => store.organizations[params.organizationId ?? activeOrganization] ?? null) as Organization | null;

  const responsive = useResponsiveValues();
  const spacing = useNormalizeSpacing(props);
  const { isActiveUserAdmin } = useAccessControl();

  const publishedContent = useMemo(() => {
    if (!currentOrganization?.content) return [] as Content[];
    const publishedContent = currentOrganization?.content.filter((c) => c.status === ContentStatus.PUBLISHED && c.type !== "offer");

    if (isLoggedIn) {
      return publishedContent as Content[];
    }

    return publishedContent.filter((c) => !Boolean(c?.isDisabled)) as Content[];
  }, [currentOrganization?.content]);

  const filteredContent = useMemo(() => {
    if (!currentOrganization) return [];

    const sanitizedContent: Content[] = publishedContent.map((c) => {
      if (!Array.isArray(c.category)) {
        return {
          ...c,
          category: c?.category ? [c?.category] : [],
        };
      }
      return { ...c, category: c.category };
    });

    // by selected content content
    if (selectedContent?.length) {
      return sanitizedContent.filter((c) => selectedContent?.includes(c.id));
    }
    // by section settings
    if (sectionSettings?.category) {
      return sanitizedContent.filter((c) => c.category.some((contentCategoryId) => contentCategoryId === sectionSettings?.category));
    }
    // by selected categories
    if (selectedCategories?.length) {
      return sanitizedContent.filter((c) => c.category.some((contentCategoryId) => selectedCategories.includes(contentCategoryId)));
    }
    // no filter
    return sanitizedContent;
  }, [currentOrganization, selectedContent, publishedContent, sectionSettings, selectedCategories, isLoggedIn]);

  const sortContent = useCallback(
    (contentList: Content[]) => {
      // const field = sortBy;
      const field = "order"; // TODO: Add this setting in the menu design options
      const acc = sort === "ascending" ? +1 : -1;
      const des = sort === "ascending" ? -1 : +1;

      if (field === "price") {
        return [...contentList].sort((a, b) => {
          if (Number(a[field]) > Number(b[field])) return acc;
          if (Number(a[field]) < Number(b[field])) return des;
          return 0;
        });
      }

      return [...contentList].sort((a, b) => {
        if (a[field] > b[field]) return acc;
        if (a[field] < b[field]) return des;
        return 0;
      });
    },
    [sortBy, sort]
  );

  const sliceContent = useCallback(
    (contentList: Content[]) => {
      return limit === "all" ? contentList : contentList.slice(0, limit);
    },
    [limit]
  );

  const finalContent = useMemo(() => {
    const slicedContent = sliceContent(filteredContent);
    const sortedContent = sortContent(slicedContent);
    return sortedContent;
  }, [filteredContent, sliceContent, sortContent]);

  useEffect(() => {
    if (finalContent.length !== 0) {
      sectionSettings.setAvailableDishes(finalContent.length);
    }
  }, [finalContent]);

  useEffect(() => {
    if (Array.isArray(selectedCategories)) {
      sectionSettings.setSelectedCategories(selectedCategories);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategories]);

  const layout = useMemo(() => {
    const columns = sectionSettings?.columns ?? 0;

    if (columns > 1) {
      return {
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap: 2,
      };
    }

    return {
      display: "flex",
      flexDirection: "column",
      gap: 2,
    };
  }, [sectionSettings?.columns]);

  if (!finalContent?.length) {
    return isActiveUserAdmin ? (
      <EmptyState ref={(ref: any) => connect(drag(ref))} title="Categoría vacía" description="Cuando agregues platos/bebidas a esta categoría se verán aquí" />
    ) : null;
  }

  if ((sectionSettings?.columns ?? 0) >= 1) {
    return (
      <Box
        key={`${id}-${responsive.viewport}`}
        ref={(ref: any) => connect(drag(ref))}
        className="dish-container"
        sx={{
          ...spacing,
          ...layout,
        }}
        gap={2}
        display="grid"
        gridTemplateColumns={`repeat(${sectionSettings?.columns}, 1fr)`}
      >
        {finalContent.map((dish) => (
          <DishTemplate dish={dish} template={sectionSettings?.template ?? "default-filled"} />
        ))}
      </Box>
    );
  }

  return (
    <div key={`${id}-${responsive.viewport}`} ref={(ref: any) => connect(drag(ref))} className="dish-container" style={{ ...spacing }}>
      <ViewSwitcher data={finalContent as unknown as Content[]} type={(sectionSettings?.columns ?? 0) > 1 ? "grid" : displayType} previewWidget={previewWidget} />
    </div>
  );
};

export default DishBlock;
